import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import {
  Search, ShoppingCart, Star, Users, Eye, DollarSign,
  ChevronDown, ChevronUp, Loader2, TrendingUp, Flame,
  ExternalLink,
} from 'lucide-react';

// ── Types ──

interface CategoryDetailPanelProps {
  siteId: string;
  sellerId: number;
  category: { id: string; name: string };
  onKeywordClick: (keyword: string) => void;
  onBestsellerSearch: (itemId: string, title: string) => void;
}

interface BestsellerCardProps {
  item: BestsellerItem;
  siteId: string;
  sellerId: number;
  onSearchAmazon: (itemId: string, title: string) => void;
}

interface BestsellerItem {
  id: string;
  position: number;
  title: string;
  price: number;
  currency: string;
  thumbnail?: string;
  sold_quantity?: number;
}

interface BestsellerDetail {
  reviews_rating?: number;
  reviews_count?: number;
  seller_count?: number;
  price_to_win?: number;
  visits_per_day?: number;
}

interface CategoryTrend {
  keyword: string;
  type: 'growing' | 'wanted' | 'popular';
  search_volume?: number;
}

// ── Keyword type styles (shared with TrendingKeywords) ──

const TYPE_STYLES: Record<string, { bg: string; text: string; icon: typeof TrendingUp }> = {
  growing: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', icon: TrendingUp },
  wanted: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: Search },
  popular: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', icon: Flame },
};

// ── Skeleton loaders ──

function SkeletonCards() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div
          key={i}
          className="rounded-lg p-3 animate-pulse"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-gray-300 dark:bg-gray-600" />
            <div className="w-12 h-12 rounded bg-gray-300 dark:bg-gray-600" />
            <div className="flex-1 space-y-2">
              <div className="h-4 rounded bg-gray-300 dark:bg-gray-600 w-3/4" />
              <div className="h-3 rounded bg-gray-300 dark:bg-gray-600 w-1/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonPills() {
  return (
    <div className="flex flex-wrap gap-2">
      {[80, 110, 96, 130, 72, 100, 88, 120].map((w, i) => (
        <div
          key={i}
          className="h-7 rounded-full animate-pulse"
          style={{ width: `${w}px`, backgroundColor: 'var(--color-bg-tertiary)' }}
        />
      ))}
    </div>
  );
}

// ── BestsellerCard ──

function BestsellerCard({ item, siteId, sellerId, onSearchAmazon }: BestsellerCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<BestsellerDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDetail = useCallback(async () => {
    if (detail) return; // Already loaded
    setLoadingDetail(true);
    try {
      const res = await api.get<BestsellerDetail>(
        `/publisher/discover/tendencias/items/${item.id}/detail?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        // Map backend nested format to flat format expected by UI
        const d = res.data as any;
        setDetail({
          reviews_rating: d.reviews?.average ?? d.reviews_rating ?? null,
          reviews_count: d.reviews?.count ?? d.reviews_count ?? null,
          seller_count: d.competition?.seller_count ?? d.seller_count ?? null,
          price_to_win: d.price_to_win?.price ?? (typeof d.price_to_win === 'number' ? d.price_to_win : null),
          visits_per_day: d.visits?.daily_avg ?? d.visits_per_day ?? null,
          gtin: d.gtin ?? null,
        } as BestsellerDetail);
      }
    } catch (err) {
      console.error('Failed to load item detail', err);
    } finally {
      setLoadingDetail(false);
    }
  }, [item.id, siteId, sellerId, detail]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) loadDetail();
  };

  const formatPrice = (price: number | null | undefined, currency: string | null | undefined) => {
    if (price == null) return '—';
    try {
      return new Intl.NumberFormat('es', { style: 'currency', currency: currency || 'USD' }).format(price);
    } catch {
      return `${currency || 'USD'} ${price.toFixed(2)}`;
    }
  };

  return (
    <div
      className="rounded-lg border transition-colors"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}
    >
      {/* Main row */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-3 p-3 text-left hover:opacity-90 transition-opacity"
      >
        {/* Position */}
        <span
          className="text-lg font-bold w-8 text-center flex-shrink-0"
          style={{ color: 'var(--color-primary)' }}
        >
          #{item.position}
        </span>

        {/* Thumbnail */}
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt=""
            className="w-12 h-12 object-contain rounded flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div
            className="w-12 h-12 rounded flex-shrink-0 flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <ShoppingCart className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
          </div>
        )}

        {/* Title + price */}
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-medium truncate"
            style={{ color: 'var(--color-text)' }}
            title={item.title}
          >
            {item.title}
          </p>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
              {formatPrice(item.price, (item as any).currency_id || item.currency)}
            </span>
            {item.sold_quantity != null && (
              <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                {item.sold_quantity.toLocaleString()} {t('tendencias.sold')}
              </span>
            )}
          </div>
        </div>

        {/* Chevron */}
        {expanded ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 pb-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-tertiary)' }} />
            </div>
          ) : detail ? (
            <div className="pt-3 space-y-3">
              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-2">
                {detail.reviews_rating != null && (
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    <Star className="w-3.5 h-3.5 text-yellow-500" />
                    <span>{detail.reviews_rating.toFixed(1)}</span>
                    {detail.reviews_count != null && (
                      <span>({detail.reviews_count.toLocaleString()})</span>
                    )}
                  </div>
                )}
                {detail.seller_count != null && (
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    <Users className="w-3.5 h-3.5" />
                    <span>{detail.seller_count} {t('tendencias.sellers')}</span>
                  </div>
                )}
                {detail.price_to_win != null && (
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>{t('tendencias.priceToWin')}: {formatPrice(detail.price_to_win, (item as any).currency_id || item.currency)}</span>
                  </div>
                )}
                {detail.visits_per_day != null && (
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    <Eye className="w-3.5 h-3.5" />
                    <span>{detail.visits_per_day.toLocaleString()} {t('tendencias.visitsDay')}</span>
                  </div>
                )}
              </div>

              {/* Search Amazon button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSearchAmazon(item.id, item.title);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                  bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300
                  hover:opacity-80 transition-opacity"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('tendencias.searchAmazon')}
              </button>
            </div>
          ) : (
            <p className="text-xs py-3 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
              {t('tendencias.noDetail')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── CategoryDetailPanel ──

type DetailTab = 'trends' | 'bestsellers';

export default function CategoryDetailPanel({
  siteId,
  sellerId,
  category,
  onKeywordClick,
  onBestsellerSearch,
}: CategoryDetailPanelProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<DetailTab>('bestsellers');
  const [trends, setTrends] = useState<CategoryTrend[]>([]);
  const [bestsellers, setBestsellers] = useState<BestsellerItem[]>([]);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);

  // Fetch trends for category
  const fetchTrends = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const res = await api.get<{ keywords: CategoryTrend[] }>(
        `/publisher/discover/tendencias/categories/${category.id}/trends?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        const arr = Array.isArray(res.data) ? res.data : (res.data as any)?.keywords || [];
        setTrends(arr.map((k: any) => ({ ...k, type: k.tier || k.type })));
      }
    } catch (err) {
      console.error('Failed to fetch category trends', err);
    } finally {
      setLoadingTrends(false);
    }
  }, [category.id, siteId, sellerId]);

  // Fetch bestsellers for category
  const fetchBestsellers = useCallback(async () => {
    setLoadingBestsellers(true);
    try {
      const res = await api.get<{ items: BestsellerItem[] }>(
        `/publisher/discover/tendencias/categories/${category.id}/bestsellers?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        setBestsellers(Array.isArray(res.data) ? res.data : (res.data as any)?.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch bestsellers', err);
    } finally {
      setLoadingBestsellers(false);
    }
  }, [category.id, siteId, sellerId]);

  // Load both on category change
  useEffect(() => {
    setTrends([]);
    setBestsellers([]);
    fetchTrends();
    fetchBestsellers();
  }, [fetchTrends, fetchBestsellers]);

  return (
    <div
      className="rounded-lg border flex flex-col"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>
          {category.name}
        </h3>
      </div>

      {/* Tab buttons */}
      <div className="flex border-b" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => setTab('trends')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors border-b-2 ${
            tab === 'trends' ? 'border-current' : 'border-transparent'
          }`}
          style={{ color: tab === 'trends' ? 'var(--color-primary)' : 'var(--color-text-tertiary)' }}
        >
          <Search className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
          {t('tendencias.tabTrends')}
        </button>
        <button
          onClick={() => setTab('bestsellers')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors border-b-2 ${
            tab === 'bestsellers' ? 'border-current' : 'border-transparent'
          }`}
          style={{ color: tab === 'bestsellers' ? 'var(--color-primary)' : 'var(--color-text-tertiary)' }}
        >
          <ShoppingCart className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
          {t('tendencias.tabBestsellers')}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '500px' }}>
        {tab === 'trends' ? (
          loadingTrends ? (
            <SkeletonPills />
          ) : trends.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: 'var(--color-text-tertiary)' }}>
              {t('tendencias.noKeywords')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {trends.map((kw, idx) => {
                const style = TYPE_STYLES[kw.type] || TYPE_STYLES.popular;
                const Icon = style.icon;
                return (
                  <button
                    key={`${kw.keyword}-${idx}`}
                    onClick={() => onKeywordClick(kw.keyword)}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                      ${style.bg} ${style.text} hover:opacity-80 transition-opacity cursor-pointer`}
                    title={kw.search_volume ? `${kw.search_volume.toLocaleString()} searches` : undefined}
                  >
                    <Icon className="w-3 h-3" />
                    {kw.keyword}
                  </button>
                );
              })}
            </div>
          )
        ) : (
          loadingBestsellers ? (
            <SkeletonCards />
          ) : bestsellers.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: 'var(--color-text-tertiary)' }}>
              {t('tendencias.noBestsellers')}
            </p>
          ) : (
            <div className="space-y-2">
              {bestsellers.map(item => (
                <BestsellerCard
                  key={item.id}
                  item={item}
                  siteId={siteId}
                  sellerId={sellerId}
                  onSearchAmazon={onBestsellerSearch}
                />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export { BestsellerCard };
export type { BestsellerItem, BestsellerDetail, BestsellerCardProps };
